// TODO: Uncomment all overrides once the shared component is ready for testing in Fluent
import '@fontsource/inter';
import transitions from '@material-ui/core/styles/transitions';
import { palette } from './palette';
import { spacing } from './spacing';
import { typography } from './typography';
import { baseFontSize, pxToRem } from './utils';
const { action, common, error, info, primary, secondary, success, warning } = palette;
/**
 * Custom overrides configuration for AgentNet. To be extended by each brand where needed.
 *
 * @returns {Overrides}
 */
export const overrides = {
    MuiCssBaseline: {
        '@global': {
            body: {
                fontFamily: ['Inter', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
                fontWeight: 400,
                fontSize: baseFontSize,
            },
        },
    },
    MuiButton: {
        root: {
            borderRadius: '4px',
            fontSize: pxToRem(14),
            fontWeight: 600,
            letterSpacing: pxToRem(0.46),
            lineHeight: pxToRem(22),
            padding: spacing(0.75, 2),
            textTransform: 'none',
            '&.containedError': {
                backgroundColor: error.main,
                color: common.white,
                '&:hover, &:focus': {
                    backgroundColor: error.dark,
                },
                '&:disabled': {
                    backgroundColor: `${error.main}26`,
                    color: `${error.main}66`,
                },
            },
            '&.containedInfo': {
                backgroundColor: info.main,
                color: common.white,
                '&:hover, &:focus': {
                    backgroundColor: info.dark,
                },
                '&:disabled': {
                    backgroundColor: `${info.main}26`,
                    color: `${info.main}66`,
                },
            },
            '&.containedWarning': {
                backgroundColor: warning.main,
                color: common.white,
                '&:hover, &:focus': {
                    backgroundColor: warning.dark,
                },
                '&:disabled': {
                    backgroundColor: `${warning.main}26`,
                    color: `${warning.main}66`,
                },
            },
            '&.containedSuccess': {
                backgroundColor: success.main,
                color: common.white,
                '&:hover, &:focus': {
                    backgroundColor: success.dark,
                },
                '&:disabled': {
                    backgroundColor: `${success.main}26`,
                    color: `${success.main}66`,
                },
            },
        },
        contained: {
            backgroundColor: primary.main,
            boxShadow: 'none',
            color: common.white,
            fontSize: pxToRem(14),
            lineHeight: pxToRem(24),
            '&:hover, &:focus': {
                backgroundColor: primary.dark,
            },
            '&:disabled': {
                backgroundColor: action.disabledBackground,
                color: action.disabled,
                cursor: 'default',
            },
            '&.MuiButton-containedSecondary': {
                '&:disabled': {
                    backgroundColor: `${secondary.main}26`,
                    color: `${secondary.main}66`,
                },
            },
        },
        containedSizeLarge: {
            fontSize: pxToRem(15),
            lineHeight: pxToRem(26),
            padding: spacing(1, 2.75),
        },
        containedSizeSmall: {
            fontSize: pxToRem(13),
            lineHeight: pxToRem(22),
            padding: spacing(0.5, 1.25),
        },
        outlined: {
            backgroundColor: common.white,
            fontSize: pxToRem(14),
            lineHeight: pxToRem(24),
            '&:hover, &:focus': {
                borderColor: primary.dark,
                backgroundColor: primary.light,
                boxShadow: 'none',
            },
            '&:disabled': {
                borderColor: action.disabledBackground,
                color: action.disabled,
            },
            '&.MuiButton-outlinedSecondary': {
                '&:disabled': {
                    borderColor: `${secondary.main}26`,
                    color: `${secondary.main}66`,
                },
            },
            '&.outlinedError': {
                backgroundColor: common.white,
                borderColor: error.main,
                color: error.main,
                '&:hover, &:focus': {
                    backgroundColor: common.white,
                    borderColor: error.dark,
                    color: error.dark,
                },
                '&:disabled': {
                    borderColor: `${error.main}26`,
                    color: `${error.main}66`,
                },
            },
            '&.outlinedInfo': {
                backgroundColor: common.white,
                borderColor: info.main,
                color: info.main,
                '&:hover, &:focus': {
                    backgroundColor: common.white,
                    borderColor: info.dark,
                    color: info.dark,
                },
                '&:disabled': {
                    borderColor: `${info.main}26`,
                    color: `${info.main}66`,
                },
            },
            '&.outlinedWarning': {
                backgroundColor: common.white,
                borderColor: warning.main,
                color: warning.main,
                '&:hover, &:focus': {
                    backgroundColor: common.white,
                    borderColor: warning.dark,
                    color: warning.dark,
                },
                '&:disabled': {
                    borderColor: `${warning.main}26`,
                    color: `${warning.main}66`,
                },
            },
            '&.outlinedSuccess': {
                backgroundColor: common.white,
                borderColor: success.main,
                color: success.main,
                '&:hover, &:focus': {
                    backgroundColor: common.white,
                    borderColor: success.dark,
                    color: success.dark,
                },
                '&:disabled': {
                    borderColor: `${success.main}26`,
                    color: `${success.main}66`,
                },
            },
        },
        outlinedSizeLarge: {
            fontSize: pxToRem(15),
            lineHeight: pxToRem(26),
            padding: spacing(1, 2.75),
        },
        outlinedSizeSmall: {
            fontSize: pxToRem(13),
            lineHeight: pxToRem(22),
            padding: spacing(0.5, 1.25),
        },
        text: {
            fontSize: pxToRem(14),
            padding: spacing(0.75, 1),
            '&.MuiButton-textSecondary': {
                '&:disabled': {
                    color: `${secondary.main}66`,
                },
            },
        },
        textSizeLarge: {
            fontSize: pxToRem(15),
            padding: spacing(1, 1.375),
        },
        textSizeSmall: {
            fontSize: pxToRem(13),
            padding: spacing(0.5, 0.625),
        },
    },
    MuiCardActionArea: {
        root: {
            transition: transitions.create('background-color', {
                duration: transitions.duration.short,
                easing: transitions.easing.easeInOut,
            }),
            '&:focus, &:hover, &:focus-within': {
                backgroundColor: palette.brand.sky25,
            },
            '&:hover $focusHighlight': {
                opacity: 0,
            },
        },
    },
    MuiIconButton: {
        root: {
            color: palette.secondary.main,
            padding: spacing(1),
            '&$disabled': {
                color: palette.text.disabled,
            },
        },
        edgeEnd: {
            marginRight: -5,
        },
        edgeStart: {
            marginLeft: -5,
        },
    },
    MuiAvatar: {
        circle: {
            backgroundColor: action.disabledBackground,
            margin: 'auto',
        },
        colorDefault: {
            backgroundColor: action.disabled,
        },
    },
    MuiListSubheader: {
        root: {
            fontSize: pxToRem(14),
        },
    },
    MuiLink: {
        root: {
            cursor: 'pointer',
        },
    },
    MuiSvgIcon: {
        root: {
            fontSize: pxToRem(24),
        },
    },
    MuiTable: {},
    MuiTableRow: {
        root: {
            transition: transitions.create('background-color', {
                duration: transitions.duration.short,
                easing: transitions.easing.easeInOut,
            }),
            '&$hover:hover': {
                backgroundColor: palette.brand.sky25,
                cursor: 'pointer',
            },
            '&$selected, &$selected:hover': {
                backgroundColor: palette.primary.light,
            },
        },
    },
    MuiTableCell: {
        root: {
            alignItems: 'center',
            lineHeight: 1.5,
            padding: spacing(0.5, 2, 0.375),
            borderBottom: 'initial',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
            borderBottomColor: palette.divider,
            '&:first-child': {
                paddingLeft: spacing(3),
            },
            '&:last-child': {
                paddingRight: spacing(3),
            },
            '& [class*="MuiBox-root"], & [class*="MuiChip-root"]': {
                width: '100%',
            },
            '&[style*="flex"]': {
                display: 'flex',
                alignItems: 'center',
                '& .MuiSkeleton-root': {
                    flex: 1,
                },
            },
            '&:not(.MuiTableCell-sizeSmall)': {
                height: spacing(7),
                '&[style*="flex"]': {
                    height: 'auto',
                    minHeight: spacing(7),
                },
            },
        },
        head: {
            ...typography.body2,
            fontWeight: 500,
            backgroundColor: '#F2F5F6',
            borderWidth: '1px 0 1px',
            borderStyle: 'solid',
            borderColor: palette.divider,
            '& > div': {
                justifyContent: 'space-between',
                flexGrow: 1,
                alignItems: 'center',
                height: '100%',
            },
            '&:hover': {
                '& [class*="MuiTableSortLabel-root"]': {
                    color: palette.text.secondary,
                    '& [class*="MuiTableSortLabel-icon"]': {
                        opacity: 0.5,
                    },
                },
            },
            '&[class*="headerSort"]': {
                '&:hover': {
                    backgroundColor: `${palette.secondary.main}26`,
                },
            },
            '&:focus-within': {
                backgroundColor: `${palette.secondary.main}26`,
                '& [class*="MuiTableSortLabel-root"][class*="Mui-focusVisible"]': {
                    color: palette.text.secondary,
                    '& [class*="MuiTableSortLabel-icon"]': {
                        opacity: 0.5,
                    },
                },
            },
        },
        paddingCheckbox: {
            '&&': {
                padding: spacing(0, 0, 0, 1.5),
            },
        },
        stickyHeader: {
            backgroundColor: '#F2F5F6',
        },
        sizeSmall: {
            padding: 0,
            // Inline editable cell styling -- This is accounting for Filled variant inputs within small table cells
            '& [class*="MuiTextField"], & [class*="MuiInputBase"]': {
                '&[class*="-marginDense"]': {
                    height: '100%',
                    margin: 0,
                    minHeight: `calc(${spacing(7)}px - 1px)`,
                },
                '& [class*="MuiFilledInput-root"]': {
                    minHeight: `calc(${spacing(7)}px - 1px)`,
                    height: '100%',
                },
                '& [class*="MuiFilledInput-root"], & [class*="MuiSelect-root"]': {
                    '& [class*="MuiInputBase-input"], &[class*="MuiSelect"]': {
                        ...typography.body2,
                        '&:not(:focus):not(:active)': {
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        },
                    },
                },
            },
            // Do not display adornments for inline edit auto complete fields unless hover/focus state
            '& [class*="MuiTextField-root"]': {
                '& > [class*="MuiFilledInput-adornedEnd"]': {
                    paddingRight: 7,
                },
                '& [class*="MuiInputAdornment-positionEnd"]': {
                    marginLeft: spacing(0.5),
                },
                '&:not(:hover)': {
                    '& [class*="MuiInputAdornment-positionEnd"], & [class*="MuiButtonBase-root"]': {
                        '& [class*="MuiIconButton"]': {
                            display: 'none',
                        },
                    },
                },
                '& > [class*="Mui-focused"]:not(:hover)': {
                    '& [class*="MuiInputAdornment-positionEnd"], & [class*="MuiButtonBase-root"]': {
                        '& [class*="MuiIconButton"]': {
                            display: 'inherit',
                        },
                    },
                },
            },
            '&:last-child': {
                paddingRight: 0,
            },
            '&.MuiTableCell-head': {
                height: spacing(5),
                '&[style*="flex"]': {
                    height: 'auto',
                    minHeight: spacing(5),
                },
            },
        },
    },
    MuiTypography: {
        root: {
            '&.lowercase': {
                textTransform: 'lowercase',
            },
            '&.uppercase': {
                textTransform: 'uppercase',
            },
        },
    },
    // MuiFormControl: {
    //   root: {
    //     '& .MuiSelect-icon': {
    //       fill: text.secondary,
    //       top: 'calc(50% - 12px)',
    //     },
    //   },
    // },
    // MuiInputBase: {
    //   root: {
    //     // MUI 4 bugfix to ensure fieldset always has a width
    //     '& [class^="PrivateNotchedOutline"]': {
    //       borderColor: custom.grayMedium,
    //       maxWidth: '1000px',
    //     },
    //   },
    // },
    // MuiInputLabel: {
    //   root: {
    //     fontSize: pxToRem(12),
    //     lineHeight: pxToRem(12),
    //     letterSpacing: pxToRem(0.15),
    //   },
    // },
    // MuiOutlinedInput: {
    //   root: {
    //     '&:hover:not($error)': {
    //       '& .MuiOutlinedInput-notchedOutline': {
    //         backgroundColor: 'white',
    //         borderColor: custom.grayMedium,
    //       },
    //     },
    //     '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    //       backgroundColor: custom.grayLight,
    //       borderColor: custom.grayMedium,
    //     },
    //   },
    //   input: {
    //     padding: spacing(1, 1.5),
    //   },
    //   multiline: {
    //     padding: spacing(1.5),
    //   },
    // },
    // MuiSelect: {
    //   root: {
    //     fontSize: pxToRem(14),
    //     lineHeight: pxToRem(21),
    //     letterSpacing: pxToRem(0.15),
    //     padding: spacing(1, 4, 0.625, 1.5),
    //   },
    //   select: {
    //     '&:focus': {
    //       backgroundColor: 'inherit',
    //     },
    //   },
    // },
    MuiList: {},
    MuiListItem: {
        root: {
            paddingLeft: spacing(3),
            '&$focusVisible': {
                backgroundColor: palette.action.hover,
            },
            '&$selected, &$selected:hover': {
                backgroundColor: palette.action.selected,
            },
        },
        button: {
            '&:hover, &:focus': {
                backgroundColor: `${primary.main}0c`,
            },
        },
        container: {
            '&.active': {
                backgroundColor: palette.action.selected,
            },
        },
        secondaryAction: {
            paddingRight: spacing(8),
        },
    },
    MuiListItemAvatar: {
        root: {
            minWidth: spacing(8),
            paddingRight: spacing(2),
            '& .MuiAvatar-root': {
                width: spacing(6),
                height: spacing(6),
                '& svg': {
                    width: spacing(3),
                    height: spacing(3),
                },
            },
            '& .MuiAvatar-colorDefault': {
                backgroundColor: '#eee',
                color: palette.text.hint,
            },
            '&:hover, &.active': {
                '& .MuiAvatar-root': {
                    backgroundColor: palette.divider,
                },
                '& svg': {
                    fill: palette.primary.main,
                    visibility: 'visible',
                },
            },
        },
    },
    MuiListItemIcon: {
        root: {
            color: palette.secondary.main,
        },
    },
    MuiListItemText: {
        primary: {
            color: palette.text.primary,
        },
        secondary: {
            color: palette.text.hint,
        },
        multiline: {
            marginTop: spacing(0.25),
            marginBottom: spacing(0.25), // 2,
        },
    },
    MuiDivider: {
        root: {
            height: 1,
            margin: 0,
        },
    },
};
